// MaturaTrigEquationFactorOpenTask.js

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import PiInput, {
  formatAffineLatexPreview,
  formatPiValueLatex,
} from "../../../../components/inputs/PiInput";
import SelectInput from "../../../../components/inputs/SelectInput";
import ExplanationMethodTabs from "../../../../components/ui/ExplanationMethodTabs";
import { selectExtendedVariant, extendedMaySource } from "../../../extendedVariantSelection";

import {
  normRat,
  ratEq,
  uniqueRats,
  ratToLatexPi,
  safeStr,
  parsePiMultiple,
} from "./helpers/piRats";
import {
  intervalLatex,
  intervalText,
  solutionsFromFamilies,
} from "./helpers/intervalAndFamilies";
import { normalizeEqLike, step2Check, isAllowedFamObj, famObjToRats } from "./helpers/checks";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


export const type = "MaturaTrigEquationFactorOpenTask";
export const cosSquaredType = "MaturaTrigCosSquaredEquationOpenTask";
export const sinSumType = "MaturaTrigSinSumEquationOpenTask";
export const tanQuadraticType = "MaturaTrigTanQuadraticEquationOpenTask";
export const quarticIdentityType = "MaturaTrigQuarticIdentityOpenTask";

/* =========================
   Variants generator (parametric)
   - lengths: pi, pi/2, 3pi/2
   - positions: 3 standard placements for each length
   ========================= */
const LENGTHS = {
  PI: normRat(1, 1),
  HALF_PI: normRat(1, 2),
  THREE_HALF_PI: normRat(3, 2),
};

const INTERVALS_BY_LENGTH = {
  PI: [
    { start: normRat(0, 1), end: normRat(1, 1) }, // [0, pi]
    { start: normRat(-1, 1), end: normRat(0, 1) }, // [-pi, 0]
    { start: normRat(-1, 2), end: normRat(1, 2) }, // [-pi/2, pi/2]
  ],
  HALF_PI: [
    { start: normRat(0, 1), end: normRat(1, 2) }, // [0, pi/2]
    { start: normRat(-1, 2), end: normRat(0, 1) }, // [-pi/2, 0]
    { start: normRat(-1, 4), end: normRat(1, 4) }, // [-pi/4, pi/4]
  ],
  THREE_HALF_PI: [
    { start: normRat(0, 1), end: normRat(3, 2) }, // [0, 3pi/2]
    { start: normRat(-3, 2), end: normRat(0, 1) }, // [-3pi/2, 0]
    { start: normRat(-3, 4), end: normRat(3, 4) }, // [-3pi/4, 3pi/4]
  ],
};

// mapping: (type, m) -> allowed interval lengths
function allowedLengths(kind, m) {
  // per your plan: lengths only PI, HALF_PI, THREE_HALF_PI
  // THREE_HALF_PI only for small m
  if (kind === "cos") {
    if (m === 2) return ["PI", "THREE_HALF_PI"];
    if (m === 3) return ["PI"]; // keep clean
    return ["HALF_PI"]; // m>=4
  }
  // sin
  if (m === 2) return ["PI", "THREE_HALF_PI"];
  if (m === 3) return ["PI"];
  return ["HALF_PI"]; // m>=4
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function cosHalfArgumentLatex(coef) {
  if (coef === 0) return "0";
  if (coef % 2 === 0) {
    const half = coef / 2;
    if (half === 1) return "x";
    return `${half}x`;
  }
  if (coef === 1) return `\\frac{x}{2}`;
  return `\\frac{${coef}x}{2}`;
}

function trigArgumentLatex(coef) {
  if (coef === 1) return "x";
  return `${coef}x`;
}

function cosHalfArgumentNormCandidates(coef) {
  if (coef % 2 === 0) {
    const half = coef / 2;
    if (half === 1) return ["cosx", "cos(x)"];
    return [`cos(${half}x)`];
  }
  if (coef === 1) return ["cos(x/2)", "cos((x)/(2))", "cos(1x/2)"];
  return [`cos(${coef}x/2)`, `cos((${coef}x)/(2))`];
}

function includesEveryFactor(expr, factors) {
  return factors.every((candidates) =>
    candidates.some((candidate) => expr.includes(candidate))
  );
}

function normalizeTrigUserText(value) {
  return normalizeEqLike(value)
    .replace(/\\neq/g, "!=")
    .replace(/≠/g, "!=")
    .replace(/tan/g, "tg")
    .replace(/\\operatorname\{tg\}/g, "tg")
    .replace(/\\tg/g, "tg")
    .replace(/\\tan/g, "tg")
    .replace(/\\sqrt\{3\}/g, "sqrt3")
    .replace(/sqrt\(3\)/g, "sqrt3")
    .replace(/sqrt3/g, "sqrt3")
    .replace(/√3/g, "sqrt3");
}

function hasTanValueEquation(raw, valueKey) {
  const s = normalizeTrigUserText(raw)
    .replace(/tg\(x\)/g, "tgx")
    .replace(/tg*x/g, "tgx")
    .replace(/\((sqrt3)\)\/\(3\)/g, "$1/3")
    .replace(/sqrt3\/3/g, "sqrt3/3");

  if (valueKey === "one") {
    return s.includes("tgx=1") || s.includes("tg(x)=1");
  }
  if (valueKey === "negOne") {
    return (
      s.includes("tgx=-1") ||
      s.includes("tg(x)=-1") ||
      s.includes("-1=tgx") ||
      s.includes("-1=tg(x)")
    );
  }

  const valueCandidates = {
    sqrt3: ["sqrt3"],
    negSqrt3: ["-sqrt3"],
    sqrt3Over3: ["sqrt3/3", "(sqrt3)/(3)"],
    negSqrt3Over3: ["-sqrt3/3", "-(sqrt3)/(3)"],
    one: ["1"],
    negOne: ["-1"],
  }[valueKey] || [];

  return valueCandidates.some(
    (v) =>
      s.includes(`tgx=${v}`) ||
      s.includes(`${v}=tgx`) ||
      s.includes(`tg(x)=${v}`) ||
      s.includes(`${v}=tg(x)`)
  );
}

function hasTanQuadraticEquation(raw, cfgOrSign) {
  const cfg = typeof cfgOrSign === "object" ? cfgOrSign : { sign: cfgOrSign };
  const s = normalizeTrigUserText(raw)
    .replace(/tg\(x\)/g, "tgx")
    .replace(/tg\^2\(x\)/g, "tgx^2")
    .replace(/tgx\^2/g, "tgx^2")
    .replace(/tg\^2x/g, "tgx^2")
    .replace(/sqrt3\*tgx/g, "sqrt3tgx")
    .replace(/tgx\*sqrt3/g, "sqrt3tgx")
    .replace(/(\d)\*tg/g, "$1tg");

  const hasSq = s.includes("tgx^2") || s.includes("tg^2x");
  if (cfg.tanFamily === "unit") {
    const hasOne = s.includes("+1") || s.includes("-1") || s.includes("=1") || s.includes("1=");
    if (!hasSq || !hasOne) return false;
    return (
      s.includes("tgx^2-1") ||
      s.includes("tg^2x-1") ||
      s.includes("tgx^2=1") ||
      s.includes("tg^2x=1") ||
      s.includes("1=tgx^2") ||
      s.includes("1=tg^2x") ||
      s.includes("-tgx^2+1") ||
      s.includes("-tg^2x+1") ||
      s.includes("-tgx^2=-1") ||
      s.includes("-tg^2x=-1") ||
      s.includes("1-tgx^2") ||
      s.includes("1-tg^2x") ||
      s.includes("3tgx^2-3") ||
      s.includes("-3tgx^2+3") ||
      s.includes("3-3tgx^2")
    );
  }

  const hasRootLinear = s.includes("2sqrt3tgx") || s.includes("-2sqrt3tgx");
  const hasConstant = s.includes("+3") || s.includes("-3") || s.includes("=3") || s.includes("3=");
  if (!hasSq || !hasRootLinear || !hasConstant) return false;

  const canonical =
    cfg.sign > 0
      ? (s.includes("-3tgx^2") || s.includes("-3tg^2x")) && s.includes("+2sqrt3tgx")
      : (s.includes("-3tgx^2") || s.includes("-3tg^2x")) && s.includes("-2sqrt3tgx");
  const multipliedByMinusOne =
    cfg.sign > 0
      ? (s.includes("3tgx^2") || s.includes("3tg^2x")) && s.includes("-2sqrt3tgx")
      : (s.includes("3tgx^2") || s.includes("3tg^2x")) && s.includes("+2sqrt3tgx");

  return canonical || multipliedByMinusOne;
}

function hasDoubleAngleEquation(raw, cfgOrSign) {
  const cfg = typeof cfgOrSign === "object" ? cfgOrSign : { sign: cfgOrSign };
  const s = normalizeTrigUserText(raw)
    .replace(/cos\(2x\)/g, "cos2x")
    .replace(/sin\(2x\)/g, "sin2x")
    .replace(/sqrt3\*sin2x/g, "sqrt3sin2x")
    .replace(/sin2x\*sqrt3/g, "sqrt3sin2x")
    .replace(/\\cos2x/g, "cos2x")
    .replace(/\\sin2x/g, "sin2x");
  if (cfg.tanFamily === "unit") {
    return s.includes("cos2x=0") || s.includes("3cos2x=0") || s.includes("-3cos2x=0");
  }
  if (hasDoubleAngleTangentAlternative(raw, cfg)) return true;
  const hasCos = s.includes("3cos2x");
  const hasSin = cfg.sign > 0 ? s.includes("+sqrt3sin2x") : s.includes("-sqrt3sin2x");
  return hasCos && hasSin;
}

function hasDoubleAngleTangentAlternative(raw, cfg) {
  const s = normalizeTrigUserText(raw).replace(/tg\(2x\)/g, "tg2x");
  const root = cfg.sign > 0 ? "-sqrt3" : "sqrt3";
  return s === `tg2x=${root}` || s === `${root}=tg2x`;
}

function hasTrigFactorization(raw, cfg = {}) {
  const s = normalizeTrigUserText(raw)
    .replace(/cos\(x\)/g, "cosx")
    .replace(/sin\(x\)/g, "sinx")
    .replace(/sqrt3\*cosx/g, "sqrt3cosx")
    .replace(/cosx\*sqrt3/g, "sqrt3cosx")
    .replace(/\\cosx/g, "cosx")
    .replace(/\\sinx/g, "sinx");
  if (cfg.tanFamily === "unit") {
    return (
      s.includes("cosx") &&
      s.includes("sinx") &&
      s.includes("=0") &&
      (s.includes("cosx^2-sinx^2") ||
        s.includes("cos^2x-sin^2x") ||
        s.includes("sinx^2-cosx^2") ||
        s.includes("sin^2x-cos^2x") ||
        s.includes(")(") ||
        s.includes(")\\cdot(") ||
        s.includes(")*("))
    );
  }
  return (
    s.includes("sqrt3cosx") &&
    s.includes("sinx") &&
    s.includes("=0") &&
    (s.includes(")(") || s.includes(")\\cdot(") || s.includes(")*("))
  );
}

function hasTanQuadraticMethod(raw, cfg) {
  return (
    hasTanQuadraticEquation(raw, cfg) ||
    hasDoubleAngleEquation(raw, cfg) ||
    hasTrigFactorization(raw, cfg)
  );
}

function hasCosNonZeroAssumption(raw) {
  const s = normalizeTrigUserText(raw).replace(/cos\(x\)/g, "cosx");
  return (
    (s.includes("cosx!=0") || s.includes("cosx<>0") || s.includes("cosx\\ne0")) ||
    (s.includes("x!=") && s.includes("pi/2") && s.includes("kpi"))
  );
}

function normalizeQuarticText(raw) {
  return normalizeTrigUserText(raw)
    .replace(/\\sin/g, "sin")
    .replace(/\\cos/g, "cos")
    .replace(/\\cdot/g, "*")
    .replace(/sin\(x\)/g, "sinx")
    .replace(/cos\(x\)/g, "cosx")
    .replace(/sin\^\{2\}\(2x\)/g, "sin2xarg2")
    .replace(/sin\^2\(2x\)/g, "sin2xarg2")
    .replace(/sin\(2x\)/g, "sin2xarg")
    .replace(/sin\^\{4\}x/g, "sin4x")
    .replace(/sinx\^4/g, "sin4x")
    .replace(/sin\^4x/g, "sin4x")
    .replace(/sin\^4\(x\)/g, "sin4x")
    .replace(/cos\^\{4\}x/g, "cos4x")
    .replace(/cosx\^4/g, "cos4x")
    .replace(/cos\^4x/g, "cos4x")
    .replace(/cos\^4\(x\)/g, "cos4x")
    .replace(/sin\^\{2\}x/g, "sin2x")
    .replace(/sinx\^2/g, "sin2x")
    .replace(/sin\^2x/g, "sin2x")
    .replace(/sin\^2\(x\)/g, "sin2x")
    .replace(/cos\^\{2\}x/g, "cos2x")
    .replace(/cosx\^2/g, "cos2x")
    .replace(/cos\^2x/g, "cos2x")
    .replace(/cos\^2\(x\)/g, "cos2x")
    .replace(/sin2xarg\^\{2\}/g, "sin2xarg2")
    .replace(/sin2xarg\^2/g, "sin2xarg2")
    .replace(/sinx\*cosx/g, "sinxcosx")
    .replace(/cosx\*sinx/g, "sinxcosx")
    .replace(/sinxcosx/g, "sinxcosx")
    .replace(/cosxsinx/g, "sinxcosx")
    .replace(/t\^\{2\}/g, "t2")
    .replace(/t\^2/g, "t2")
    .replace(/t\*t/g, "t2")
    .replace(/\+/g, "+")
    .replace(/\(([^()]*)\)/g, "$1");
}

function hasQuarticSin2Transform(raw, sign) {
  const s = normalizeQuarticText(raw);
  const rhs = sign > 0 ? "sinxcosx" : "-sinxcosx";
  const poly = sign > 0 ? "sin2xarg2+sin2xarg-2=0" : "sin2xarg2-sin2xarg-2=0";
  const factoredA =
    sign > 0
      ? "sin2xarg-1sin2xarg+2=0"
      : "sin2xarg+1sin2xarg-2=0";
  const factoredB =
    sign > 0
      ? "sin2xarg+2sin2xarg-1=0"
      : "sin2xarg-2sin2xarg+1=0";
  return (
    s.includes(poly) ||
    s.includes(factoredA) ||
    s.includes(factoredB) ||
    s.includes(`1-2sin2xcos2x=${rhs}`) ||
    s.includes(`${rhs}=1-2sin2xcos2x`)
  );
}

function hasQuarticSin2Equation(raw, sign) {
  const s = normalizeQuarticText(raw);
  return sign > 0
    ? s.includes("sin2xarg=1") || s.includes("1=sin2xarg")
    : s.includes("sin2xarg=-1") || s.includes("-1=sin2xarg");
}

function samePiFamily(user, expected) {
  if (!user || !expected || !ratEq(user.b, expected.b)) return false;
  const diff = normRat(
    user.a.num * expected.a.den - expected.a.num * user.a.den,
    user.a.den * expected.a.den
  );
  if (!diff || diff.num === 0) return true;
  const quotient = normRat(diff.num * user.b.den, diff.den * user.b.num);
  return quotient && quotient.den === 1;
}

function buildCosConfig(m, interval) {
  const mMinus = m - 1;
  const mPlus = m + 1;

  const equationLatex = `\\cos(${trigArgumentLatex(mMinus)})+2\\cos^2(${trigArgumentLatex(m)})+\\cos(${trigArgumentLatex(mPlus)})=0`;

  // cos((m-1)x)+cos((m+1)x)=2cos(mx)cos(x)
  const step1Latex = `2\\cos(${trigArgumentLatex(m)})\\big(\\cos x+\\cos(${trigArgumentLatex(m)})\\big)=0`;

  const eq2_base = [`\\cos(${trigArgumentLatex(m)})=0`, `\\cos x+\\cos(${trigArgumentLatex(m)})=0`];
  const eq2_split = [
    `\\cos(${cosHalfArgumentLatex(mPlus)})=0`,
    `\\cos(${cosHalfArgumentLatex(mMinus)})=0`,
  ];
  const finalProductLatex = `\\cos(${trigArgumentLatex(m)})\\cos\\left(${cosHalfArgumentLatex(mPlus)}\\right)\\cos\\left(${cosHalfArgumentLatex(mMinus)}\\right)=0`;

  // families:
  // cos(mx)=0 -> x = pi/(2m) + k*pi/m
  const famA = { a: normRat(1, 2 * m), b: normRat(1, m) };
  // cos((m+1)x/2)=0 -> x = pi/(m+1) + k*2pi/(m+1)
  const famB1 = { a: normRat(1, mPlus), b: normRat(2, mPlus) };
  // cos((m-1)x/2)=0 -> x = pi/(m-1) + k*2pi/(m-1)  (m-1>=1)
  const famB2 = { a: normRat(1, mMinus), b: normRat(2, mMinus) };

  return {
    variant: `cos_m${m}`,
    kind: "cos",
    m,
    interval,
    equationLatex,
    step1Latex,
    finalProductLatex,
    eq2_base,
    eq2_split,
    families: [famA, famB1, famB2],
  };
}

function buildSinConfig(m, interval) {
  // sin x + sin((2m-1)x) + sin(2mx) = 0
  const a2 = 2 * m - 1;
  const a3 = 2 * m;

  const equationLatex = `\\sin x+\\sin(${trigArgumentLatex(a2)})+\\sin(${trigArgumentLatex(a3)})=0`;

  // => 2 sin(mx) (cos((m-1)x)+cos(mx)) = 0
  const step1Latex = `2\\sin(${trigArgumentLatex(m)})\\big(\\cos(${trigArgumentLatex(m - 1)})+\\cos(${trigArgumentLatex(m)})\\big)=0`;

  const eq2_base = [
    `\\sin(${trigArgumentLatex(m)})=0`,
    `\\cos(${trigArgumentLatex(m - 1)})+\\cos(${trigArgumentLatex(m)})=0`,
  ];
  const eq2_split = [
    `\\cos(${cosHalfArgumentLatex(2 * m - 1)})=0`,
    `\\cos(\\frac{x}{2})=0`,
  ];
  const finalProductLatex = `\\sin(${trigArgumentLatex(m)})\\cos\\left(${cosHalfArgumentLatex(2 * m - 1)}\\right)\\cos\\left(\\frac{x}{2}\\right)=0`;

  // families:
  // sin(mx)=0 -> x = 0 + k*pi/m
  const famA = { a: normRat(0, 1), b: normRat(1, m) };
  // cos((2m-1)x/2)=0 -> x = pi/(2m-1) + k*2pi/(2m-1)
  const famB1 = { a: normRat(1, 2 * m - 1), b: normRat(2, 2 * m - 1) };
  // cos(x/2)=0 -> x = pi + 2kpi
  const famB2 = { a: normRat(1, 1), b: normRat(2, 1) };

  return {
    variant: `sin_m${m}`,
    kind: "sin",
    m,
    interval,
    equationLatex,
    step1Latex,
    finalProductLatex,
    eq2_base,
    eq2_split,
    families: [famA, famB1, famB2],
  };
}

function buildTanQuadraticConfig(sign, interval, tanFamily = "sqrt3") {
  if (tanFamily === "unit") {
    const equationLatex =
      sign > 0
        ? "\\cos^2x - \\sin^2x=0"
        : "\\sin^2x - \\cos^2x=0";
    const tanEquationLatex =
      sign > 0
        ? "1-\\operatorname{tg}^2x=0"
        : "\\operatorname{tg}^2x-1=0";
    const doubleAngleEquation = sign > 0 ? "\\cos(2x)=0" : "-\\cos(2x)=0";

    return {
      variant: `tan_quad_unit_${sign > 0 ? "plus" : "minus"}`,
      kind: "tanQuadratic",
      tanFamily,
      sign,
      interval,
      equationLatex,
      assumptionLatex: "\\cos x\\neq 0",
      tanEquationLatex,
      alternativeLatex: "\\operatorname{tg} x=1\\;\\lor\\;\\operatorname{tg} x=-1",
      roots: [
        { key: "one", latex: "1", family: { a: normRat(1, 4), b: normRat(1, 1) } },
        { key: "negOne", latex: "-1", family: { a: normRat(-1, 4), b: normRat(1, 1) } },
      ],
      families: [{ a: normRat(1, 4), b: normRat(1, 2) }],
      acceptedFamilies: [
        { a: normRat(1, 4), b: normRat(1, 2) },
        { a: normRat(1, 4), b: normRat(1, 1) },
        { a: normRat(-1, 4), b: normRat(1, 1) },
      ],
      doubleFamily: { a: normRat(1, 4), b: normRat(1, 2) },
    };
  }

  const middleSign = sign > 0 ? "+" : "-";
  const rootA =
    sign > 0
      ? { key: "sqrt3", latex: "\\sqrt{3}", family: { a: normRat(1, 3), b: normRat(1, 1) } }
      : { key: "negSqrt3", latex: "-\\sqrt{3}", family: { a: normRat(-1, 3), b: normRat(1, 1) } };
  const rootB =
    sign > 0
      ? { key: "negSqrt3Over3", latex: "-\\dfrac{\\sqrt{3}}{3}", family: { a: normRat(-1, 6), b: normRat(1, 1) } }
      : { key: "sqrt3Over3", latex: "\\dfrac{\\sqrt{3}}{3}", family: { a: normRat(1, 6), b: normRat(1, 1) } };

  return {
    variant: `tan_quad_${sign > 0 ? "plus" : "minus"}`,
    kind: "tanQuadratic",
    tanFamily,
    sign,
    interval,
    equationLatex: `3\\cos^2x ${middleSign} \\sqrt{3}\\sin(2x) - 3\\sin^2x=0`,
    assumptionLatex: "\\cos x\\neq 0",
    tanEquationLatex: `-3\\operatorname{tg}^2x ${middleSign} 2\\sqrt{3}\\operatorname{tg} x + 3 = 0`,
    alternativeLatex: `\\operatorname{tg} x=${rootA.latex}\\;\\lor\\;\\operatorname{tg} x=${rootB.latex}`,
    roots: [rootA, rootB],
    families: [rootA.family, rootB.family],
    acceptedFamilies: [rootA.family, rootB.family,
      { a: normRat(sign > 0 ? -1 : 1, 6), b: normRat(1, 2) }],
  };
}

function generateConfig(forcedKind = null) {
  const kind = forcedKind || pick(["cos", "sin"]);

  const mChoices = kind === "cos" ? [2, 3, 4, 5] : [2, 3, 4];
  const m = pick(mChoices);

  const lenKeys = allowedLengths(kind, m);
  const lenKey = pick(lenKeys);

  const interval = pick(INTERVALS_BY_LENGTH[lenKey]);

  return kind === "cos" ? buildCosConfig(m, interval) : buildSinConfig(m, interval);
}

const TAN_INTERVALS = [
    { start: normRat(-1, 1), end: normRat(1, 1) },
    { start: normRat(0, 1), end: normRat(1, 1) },
    { start: normRat(-1, 2), end: normRat(1, 2) },
    { start: normRat(-1, 1), end: normRat(0, 1) },
  ];
const MAY_TAN_CONFIGS = [1, -1].flatMap((sign) => TAN_INTERVALS.map((interval) => ({ sign, interval })))
  .filter((c) => !(c.sign === 1 && ratEq(c.interval.start, normRat(-1, 1)) && ratEq(c.interval.end, normRat(1, 1))));
export function getMayVariantCount() { return MAY_TAN_CONFIGS.length; }

function buildQuarticIdentityConfig(sign, interval, formKey) {
  const signLatex = sign > 0 ? "" : "-";
  const signWord = sign > 0 ? "plus" : "minus";
  const equationForms = {
    sinLeft: `\\sin^4x=${signLatex}\\sin x\\cos x-\\cos^4x`,
    cosLeft: `\\cos^4x=${signLatex}\\sin x\\cos x-\\sin^4x`,
    sumLeft: `${signLatex}\\sin x\\cos x=\\sin^4x+\\cos^4x`,
  };
  const equationLatex = equationForms[formKey] || equationForms.sinLeft;
  const sumLatex =
    sign > 0
      ? "\\sin^4x+\\cos^4x=\\sin x\\cos x"
      : "\\sin^4x+\\cos^4x=-\\sin x\\cos x";
  const sin2PolynomialLatex =
    sign > 0 ? "\\sin^2(2x)+\\sin(2x)-2=0" : "\\sin^2(2x)-\\sin(2x)-2=0";
  const sin2FactoredLatex =
    sign > 0
      ? "(\\sin(2x)-1)(\\sin(2x)+2)=0"
      : "(\\sin(2x)+1)(\\sin(2x)-2)=0";
  const sin2RootsLatex =
    sign > 0
      ? "\\sin(2x)=1\\;\\lor\\;\\sin(2x)=-2"
      : "\\sin(2x)=-1\\;\\lor\\;\\sin(2x)=2";
  const sinDoubleLatex = sign > 0 ? "\\sin(2x)=1" : "\\sin(2x)=-1";
  const family = sign > 0
    ? { a: normRat(1, 4), b: normRat(1, 1) }
    : { a: normRat(-1, 4), b: normRat(1, 1) };

  return {
    variant: `quartic_${signWord}_${formKey}`,
    kind: "quarticIdentity",
    sign,
    formKey,
    interval,
    equationLatex,
    sumLatex,
    sin2PolynomialLatex,
    sin2FactoredLatex,
    sin2RootsLatex,
    sinDoubleLatex,
    families: [family],
  };
}

function generateQuarticIdentityConfig() {
  const sign = pick([1, -1]);
  const formKey = pick(["sinLeft", "cosLeft", "sumLeft"]);
  const interval = pick([
    { start: normRat(-1, 1), end: normRat(2, 1) },
    { start: normRat(0, 1), end: normRat(2, 1) },
    { start: normRat(-1, 1), end: normRat(1, 1) },
    { start: normRat(-1, 2), end: normRat(3, 2) },
  ]);
  return buildQuarticIdentityConfig(sign, interval, formKey);
}

/* =========================
   Task generator
   ========================= */
export function generateTask(options = {}) {
  const cfg = generateConfig(options.kind);
  const solutions = solutionsFromFamilies(cfg.families, cfg.interval);

  return {
    type: options.typeOverride || type,
    data: {
      cfg,
      interval: cfg.interval,
      solutions,
      maxPoints: 4,
    },
  };
}

export function generateCosSquaredTask() {
  return generateTask({ kind: "cos", typeOverride: cosSquaredType });
}

export function generateSinSumTask() {
  return generateTask({ kind: "sin", typeOverride: sinSumType });
}

export function generateTanQuadraticTask(options = {}) {
  const original = options.variant === "original";
  const config = original ? { sign: 1, interval: TAN_INTERVALS[0] } : selectExtendedVariant(MAY_TAN_CONFIGS, options);
  const cfg = buildTanQuadraticConfig(config.sign, config.interval);
  const solutions = solutionsFromFamilies(cfg.families, cfg.interval);

  return {
    type: tanQuadraticType,
    source: extendedMaySource("9", original),
    variantIndex: config.variantIndex,
    data: {
      cfg,
      interval: cfg.interval,
      solutions,
      maxPoints: 5,
    },
  };
}

export function generateQuarticIdentityTask() {
  const cfg = generateQuarticIdentityConfig();
  const solutions = solutionsFromFamilies(cfg.families, cfg.interval);

  return {
    type: quarticIdentityType,
    data: {
      cfg,
      interval: cfg.interval,
      solutions,
      maxPoints: 4,
    },
  };
}

function tanQuadraticFactorLatex(cfgOrSign) {
  const cfg = typeof cfgOrSign === "object" ? cfgOrSign : { sign: cfgOrSign };
  if (cfg.tanFamily === "unit") {
    return cfg.sign > 0
      ? "(\\cos x-\\sin x)(\\cos x+\\sin x)=0"
      : "(\\sin x-\\cos x)(\\sin x+\\cos x)=0";
  }
  const sign = cfg.sign;
  if (sign > 0) {
    return `(\\sqrt{3}\\cos x-\\sin x)(\\sqrt{3}\\cos x+3\\sin x)=0`;
  }
  return `(\\sqrt{3}\\cos x-3\\sin x)(\\sqrt{3}\\cos x+\\sin x)=0`;
}

/* =========================
   Render task statement
   ========================= */
export function renderTask({ data }) {
  const { cfg, interval } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>Rozwiąż równanie</p>
      <BlockMath math={cfg.equationLatex} />
      <p>
        w przedziale <InlineMath math={intervalLatex(interval)} />.
      </p>
      <p>Zapisz tok rozumowania w krokach.</p>
    </div>
  );
}

/* =========================
   Render inputs
   ========================= */
function renderTanQuadraticInput({ data }, userAnswer = {}, onChange, disabled) {
  const v = userAnswer || {};
  const { interval } = data;
  const eqs = Array.isArray(v.eqs) ? v.eqs : [];
  const fams = Array.isArray(v.fams) ? v.fams : [];
  const xs = Array.isArray(v.xs) ? v.xs : [];
  const setValue = (patch) => onChange?.({ ...v, ...patch });

  const eqCountOptions = [1, 2].map((k) => ({ value: k, label: String(k) }));
  const famCountOptions = [1, 2].map((k) => ({ value: k, label: String(k) }));
  const solCountOptions = [1, 2, 3, 4, 5].map((k) => ({ value: k, label: String(k) }));
  const selectCount = (raw) => (raw === "" ? "" : Number(raw));
  const toSelectedCount = (value) =>
    typeof value === "number" && Number.isInteger(value) ? value : 0;

  const eqCount =
    typeof v.eqCount === "number" && Number.isInteger(v.eqCount) ? v.eqCount : "";
  const famCount =
    typeof v.famCount === "number" && Number.isInteger(v.famCount) ? v.famCount : "";
  const solCount =
    typeof v.solCount === "number" && Number.isInteger(v.solCount) ? v.solCount : "";

  const setEqAt = (i, val) => {
    const next = eqs.slice();
    next[i] = val;
    setValue({ eqs: next });
  };
  const setFamAt = (i, famObj) => {
    const next = fams.slice();
    next[i] = famObj;
    setValue({ fams: next });
  };
  const setXAt = (i, val) => {
    const next = xs.slice();
    next[i] = val;
    setValue({ xs: next });
  };

  return (
    <div className="flex flex-col gap-4" style={{ textAlign: "left" }}>
      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 1 (2 pkt).</strong> Przekształć równanie do postaci równoważnej.
        </div>
        <EquationInput
          value={v.tanEquation || ""}
          onChange={(val) => setValue({ tanEquation: val })}
          disabled={disabled}
          placeholder="np. -3tg^2x+2sqrt(3)tgx+3=0"
          showPreview
        />
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Krok 2 (1 pkt).</strong> Zapisz równania alternatywne.
        </div>
        <div style={{ marginBottom: 10, maxWidth: 240 }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
            Wybierz liczbę równań:
          </div>
          <SelectInput
            value={eqCount}
            onChange={(e) => setValue({ eqCount: selectCount(e.target.value) })}
            disabled={disabled}
            options={eqCountOptions}
          />
        </div>
        <div className="flex flex-col gap-2">
          {Array.from({ length: toSelectedCount(eqCount) }).map((_, i) => (
            <EquationInput
              key={i}
              value={eqs[i] ?? ""}
              onChange={(val) => setEqAt(i, val)}
              disabled={disabled}
              placeholder="np. tgx=sqrt(3)"
              showPreview
            />
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Krok 3 (1 pkt).</strong> Podaj rozwiązania ogólne.
        </div>
        <div style={{ marginBottom: 10, maxWidth: 260 }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
            Wybierz liczbę rodzin rozwiązań:
          </div>
          <SelectInput
            value={famCount}
            onChange={(e) => setValue({ famCount: selectCount(e.target.value) })}
            disabled={disabled}
            options={famCountOptions}
          />
        </div>
        <div className="flex flex-col gap-3">
          {Array.from({ length: toSelectedCount(famCount) }).map((_, i) => {
            const cur =
              fams[i] && typeof fams[i] === "object"
                ? fams[i]
                : { raw: fams[i] ?? "" };
            return (
              <PiInput
                key={i}
                value={cur}
                onChange={(val) => setFamAt(i, val)}
                disabled={disabled}
                label={"S_{" + (i + 1) + "}"}
                placeholder="np. pi/3+k*pi"
                allowAffine
              />
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Krok 4 (1 pkt).</strong> Wpisz rozwiązania z przedziału{" "}
          <InlineMath math={intervalLatex(interval)} />.
        </div>
        <div style={{ marginBottom: 10, maxWidth: 260 }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
            Wybierz liczbę rozwiązań:
          </div>
          <SelectInput
            value={solCount}
            onChange={(e) => setValue({ solCount: selectCount(e.target.value) })}
            disabled={disabled}
            options={solCountOptions}
          />
        </div>
        <div className="flex flex-col gap-2">
          {Array.from({ length: toSelectedCount(solCount) }).map((_, i) => (
            <PiInput
              key={i}
              value={xs[i] ?? ""}
              onChange={(val) => setXAt(i, val)}
              disabled={disabled}
              label={"x_{" + (i + 1) + "}"}
              placeholder="np. -2pi/3"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function renderQuarticIdentityInput({ data }, userAnswer = {}, onChange, disabled) {
  const v = userAnswer || {};
  const { interval } = data;
  const xs = Array.isArray(v.xs) ? v.xs : [];
  const setValue = (patch) => onChange?.({ ...v, ...patch });
  const solCountOptions = [1, 2, 3, 4].map((k) => ({ value: k, label: String(k) }));
  const solCount =
    typeof v.solCount === "number" && Number.isInteger(v.solCount) ? v.solCount : "";
  const selectCount = (raw) => (raw === "" ? "" : Number(raw));
  const toSelectedCount = (value) =>
    typeof value === "number" && Number.isInteger(value) ? value : 0;
  const setXAt = (i, val) => {
    const next = xs.slice();
    next[i] = val;
    setValue({ xs: next });
  };

  return (
    <div className="flex flex-col gap-4" style={{ textAlign: "left" }}>
      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 1 (1 pkt).</strong>{" "}
          {"Przekszta\u0142\u0107 r\u00f3wnanie do postaci z funkcj\u0105 "}
          <InlineMath math={"\\sin(2x)"} />.
        </div>
        <EquationInput
          value={v.step1 || ""}
          onChange={(val) => setValue({ step1: val })}
          disabled={disabled}
          placeholder="np. sin^2(2x)+sin(2x)-2=0"
          showPreview
        />
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 2 (1 pkt).</strong>{" "}
          {"Zapisz r\u00f3wnanie trygonometryczne do rozwi\u0105zania."}
        </div>
        <div className="flex flex-col gap-2">
          <EquationInput
            value={v.sin2Equation || ""}
            onChange={(val) => setValue({ sin2Equation: val })}
            disabled={disabled}
            placeholder="np. sin(2x)=1"
            showPreview
          />
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Krok 3 (1 pkt).</strong> {"Podaj rozwi\u0105zanie og\u00f3lne."}
        </div>
        <PiInput
          value={v.family && typeof v.family === "object" ? v.family : { raw: v.family ?? "" }}
          onChange={(val) => setValue({ family: val })}
          disabled={disabled}
          label="S"
          placeholder="np. pi/4+k*pi"
          allowAffine
        />
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Krok 4 (1 pkt).</strong> {"Wpisz rozwi\u0105zania z przedzia\u0142u"}{" "}
          <InlineMath math={intervalLatex(interval)} />.
        </div>
        <div style={{ marginBottom: 10, maxWidth: 260 }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
            {"Wybierz liczb\u0119 rozwi\u0105za\u0144:"}
          </div>
          <SelectInput
            value={solCount}
            onChange={(e) => setValue({ solCount: selectCount(e.target.value) })}
            disabled={disabled}
            options={solCountOptions}
          />
        </div>
        <div className="flex flex-col gap-2">
          {Array.from({ length: toSelectedCount(solCount) }).map((_, i) => (
            <PiInput
              key={i}
              value={xs[i] ?? ""}
              onChange={(val) => setXAt(i, val)}
              disabled={disabled}
              label={"x_{" + (i + 1) + "}"}
              placeholder="np. -3pi/4"
            />
          ))}
        </div>
      </div>
    </div>
  );
}


export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  if (data?.cfg?.kind === "quarticIdentity") {
    return renderQuarticIdentityInput({ data }, userAnswer, onChange, disabled);
  }
  if (data?.cfg?.kind === "tanQuadratic") {
    return renderTanQuadraticInput({ data }, userAnswer, onChange, disabled);
  }
  const v = userAnswer || {};
  const { interval } = data;

  const eqs = Array.isArray(v.eqs) ? v.eqs : [];
  const fams = Array.isArray(v.fams) ? v.fams : [];
  const xs = Array.isArray(v.xs) ? v.xs : [];

  const setValue = (patch) => onChange?.({ ...v, ...patch });

  const eqCountOptions = [1, 2, 3].map((k) => ({ value: k, label: String(k) }));
  const famCountOptions = [1, 2, 3, 4].map((k) => ({ value: k, label: String(k) }));
  const solCountOptions = [1, 2, 3, 4, 5, 6, 7].map((k) => ({ value: k, label: String(k) }));

  const eqCount =
    typeof v.eqCount === "number" && Number.isInteger(v.eqCount) ? v.eqCount : "";
  const famCount =
    typeof v.famCount === "number" && Number.isInteger(v.famCount) ? v.famCount : "";
  const solCount =
    typeof v.solCount === "number" && Number.isInteger(v.solCount) ? v.solCount : "";

  const toSelectedCount = (value) =>
    typeof value === "number" && Number.isInteger(value) ? value : 0;
  const selectCount = (raw) => (raw === "" ? "" : Number(raw));

  const setEqAt = (i, val) => {
    const next = eqs.slice();
    next[i] = val;
    setValue({ eqs: next });
  };

  const setFamAt = (i, famObj) => {
    const next = fams.slice();
    next[i] = famObj;
    setValue({ fams: next });
  };

  const setXAt = (i, val) => {
    const next = xs.slice();
    next[i] = val;
    setValue({ xs: next });
  };

  return (
    <div className="flex flex-col gap-4" style={{ textAlign: "left" }}>
      <OpenTaskScoringHint variant="independent" />
      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 1 (1 pkt).</strong> Przekształć równanie.
        </div>
        <EquationInput
          value={v.step1 || ""}
          onChange={(val) => setValue({ step1: val })}
          disabled={disabled}
          placeholder="Wpisz przekształcone równanie..."
          showPreview
        />
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Krok 2 (1 pkt).</strong> Zapisz równania do rozwiązania.
        </div>

        <div style={{ marginBottom: 10, maxWidth: 240 }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
            Wybierz liczbę równań:
          </div>
          <SelectInput
            value={eqCount}
            onChange={(e) => setValue({ eqCount: selectCount(e.target.value) })}
            disabled={disabled}
            options={eqCountOptions}
          />
        </div>

        <div className="flex flex-col gap-2">
          {Array.from({ length: toSelectedCount(eqCount) }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div>
                <InlineMath math={`(\\,${i + 1}\\,)\\;`} />
              </div>
              <EquationInput
                value={eqs[i] ?? ""}
                onChange={(val) => setEqAt(i, val)}
                disabled={disabled}
                placeholder="np. cos(3x)=0"
                showPreview
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Krok 3 (1 pkt).</strong> Podaj rozwiązania ogólne.
        </div>

        <div style={{ marginBottom: 10, maxWidth: 260 }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
            Wybierz liczbę rodzin rozwiązań:
          </div>
          <SelectInput
            value={famCount}
            onChange={(e) => setValue({ famCount: selectCount(e.target.value) })}
            disabled={disabled}
            options={famCountOptions}
          />
        </div>

        <div className="flex flex-col gap-3">
          {Array.from({ length: toSelectedCount(famCount) }).map((_, i) => {
            const cur =
              fams[i] && typeof fams[i] === "object"
                ? fams[i]
                : { raw: fams[i] ?? "" };

            return (
              <div key={i} className="flex flex-col gap-2">
                <div>
                  <InlineMath math={`S_{${i + 1}}:`} />
                </div>

                <PiInput
                  value={cur}
                  onChange={(val) => setFamAt(i, val)}
                  disabled={disabled}
                  label={`S_{${i + 1}}`}
                  placeholder="np. π/6+k*pi/3"
                  allowAffine
                />
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Krok 4 (1 pkt).</strong> Wpisz rozwiązania z przedziału{" "}
          <InlineMath math={intervalLatex(interval)} />.
        </div>

        <div style={{ marginBottom: 10, maxWidth: 260 }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
            Wybierz liczbę rozwiązań:
          </div>
          <SelectInput
            value={solCount}
            onChange={(e) => setValue({ solCount: selectCount(e.target.value) })}
            disabled={disabled}
            options={solCountOptions}
          />
        </div>

        <div className="flex flex-col gap-2">
          {Array.from({ length: toSelectedCount(solCount) }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div>
                <InlineMath math={`x_{${i + 1}}=`} />
              </div>
              <PiInput
                value={xs[i] ?? ""}
                onChange={(val) => setXAt(i, val)}
                disabled={disabled}
                label={`x_{${i + 1}}`}
                placeholder="np. π/6"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* =========================
   Main checkAnswer
   ========================= */
export function checkAnswer({ data }, userAnswer = {}) {
  const { cfg, interval, solutions, maxPoints } = data;

  if (cfg.kind === "quarticIdentity") {
    const step1Ok = hasQuarticSin2Transform(userAnswer.step1, cfg.sign);
    const step2Ok = hasQuarticSin2Equation(userAnswer.sin2Equation, cfg.sign);

    const famObj = userAnswer.family && typeof userAnswer.family === "object"
      ? userAnswer.family
      : { raw: userAnswer.family ?? "" };
    const parsedFam = famObjToRats(famObj);
    const step3Ok = parsedFam ? samePiFamily(parsedFam, cfg.families[0]) : false;

    const xs = Array.isArray(userAnswer.xs) ? userAnswer.xs : [];
    const parsed = xs.map((v) => parsePiMultiple(safeStr(v))).filter((v) => v != null);
    const uniqUser = uniqueRats(parsed);
    const expected = solutions.map((r) => normRat(r.num, r.den));
    const correctCount = uniqUser.filter((u) => expected.some((e) => ratEq(e, u))).length;
    const wrongCount = uniqUser.filter((u) => !expected.some((e) => ratEq(e, u))).length;
    const step4Ok = correctCount === expected.length && wrongCount === 0
      && xs.filter((v) => safeStr(v).trim()).every((v) => parsePiMultiple(safeStr(v)) != null);

    let score = 0;
    if (step1Ok) score += 1;
    if (step2Ok) score += 1;
    if (step3Ok) score += 1;
    if (step4Ok) score += 1;

    return {
      isCorrect: step1Ok && step2Ok && step3Ok && step4Ok,
      score,
      maxScore: maxPoints,
      parts: {
        ["krok 1: przekszta\u0142cenie do funkcji sin(2x)"]: step1Ok,
        ["krok 2: r\u00f3wnanie trygonometryczne"]: step2Ok,
        ["krok 3: rozwi\u0105zanie og\u00f3lne"]: step3Ok,
        [`krok 4: rozwi\u0105zania w przedziale ${intervalText(interval)}`]: step4Ok,
      },
    };
  }

  if (cfg.kind === "tanQuadratic") {
    const methodOk = hasTanQuadraticMethod(userAnswer.tanEquation, cfg);

    const eqsRaw = Array.isArray(userAnswer.eqs) ? userAnswer.eqs : [];
    const eqsNorm = eqsRaw.map((e) => normalizeTrigUserText(safeStr(e))).filter(Boolean);
    const alternativesOk = (
      cfg.roots.every((root) => eqsNorm.some((e) => hasTanValueEquation(e, root.key))) &&
      eqsNorm.every((e) => cfg.roots.some((root) => hasTanValueEquation(e, root.key)))
    ) || (cfg.tanFamily !== "unit" && eqsNorm.length > 0
      && eqsNorm.every((e) => hasDoubleAngleTangentAlternative(e, cfg)));

    const famsRaw = Array.isArray(userAnswer.fams) ? userAnswer.fams : [];
    const famObjs = famsRaw
      .filter((f) => f && typeof f === "object")
      .filter(
        (f) =>
          String(f.raw ?? "").trim() ||
          String(f.a ?? "").trim() ||
          String(f.b ?? "").trim() ||
          String(f.affine?.aRaw ?? "").trim() ||
          String(f.affine?.bRaw ?? "").trim()
      );
    const hasWrongFam = famObjs.some((f) => !isAllowedFamObj(f, cfg));
    const parsedFamilies = famObjs.map(famObjToRats).filter(Boolean);
    const compactFamily = { a: normRat(cfg.sign > 0 ? -1 : 1, 6), b: normRat(1, 2) };
    let familiesOk = !hasWrongFam && (parsedFamilies.some((f) => samePiFamily(f, compactFamily))
      || cfg.families.every((expected) => parsedFamilies.some((f) => samePiFamily(f, expected))));
    if (cfg.tanFamily === "unit") {
      const parsedFams = famObjs.map(famObjToRats).filter(Boolean);
      const compact = { a: normRat(1, 4), b: normRat(1, 2) };
      const positive = { a: normRat(1, 4), b: normRat(1, 1) };
      const negative = { a: normRat(-1, 4), b: normRat(1, 1) };
      const hasCompact = parsedFams.some((f) => samePiFamily(f, compact));
      const hasPositive = parsedFams.some((f) => samePiFamily(f, positive));
      const hasNegative = parsedFams.some((f) => samePiFamily(f, negative));
      familiesOk = !hasWrongFam && (hasCompact || (hasPositive && hasNegative));
    }

    const xs = Array.isArray(userAnswer.xs) ? userAnswer.xs : [];
    const parsed = xs.map((v) => parsePiMultiple(safeStr(v))).filter((v) => v != null);
    const uniqUser = uniqueRats(parsed);
    const expected = solutions.map((r) => normRat(r.num, r.den));
    const correctCount = uniqUser.filter((u) => expected.some((e) => ratEq(e, u))).length;
    const wrongCount = uniqUser.filter((u) => !expected.some((e) => ratEq(e, u))).length;
    const intervalOk = correctCount === expected.length && wrongCount === 0
      && xs.filter((v) => safeStr(v).trim()).every((v) => parsePiMultiple(safeStr(v)) != null);

    let score = 0;
    if (methodOk) score += 2;
    if (alternativesOk) score += 1;
    if (familiesOk) score += 1;
    if (intervalOk) score += 1;

    return {
      isCorrect: methodOk && alternativesOk && familiesOk && intervalOk,
      score,
      maxScore: maxPoints,
      parts: {
        "krok 1: poprawne przekształcenie równania": methodOk,
        "krok 2: równania alternatywne": alternativesOk,
        "krok 3: rozwiązania ogólne": familiesOk,
        ["krok 4: rozwiązania w przedziale " + intervalText(interval)]: intervalOk,
      },
    };
  }

  // STEP 1 (tolerant): look for key tokens depending on kind
  const s1 = normalizeEqLike(safeStr(userAnswer.step1));
  let step1Ok = false;

  if (cfg.kind === "cos") {
    const hasCosMx = s1.includes(`cos(${cfg.m}x)`) || s1.includes(`cos(${cfg.m}*x)`);
    const hasCosX = s1.includes("cosx") || s1.includes("cos(x)");
    const hasCosSq = s1.includes(`cos^2(${cfg.m}x)`) || s1.includes(`cos(${cfg.m}x)^2`);
    const hasFactoredSum =
      hasCosMx &&
      (s1.includes(`cosx+cos(${cfg.m}x)`) ||
        s1.includes(`cos(${cfg.m}x)+cosx`) ||
        s1.includes(`cos(x)+cos(${cfg.m}x)`) ||
        s1.includes(`cos(${cfg.m}x)+cos(x)`));
    const hasFinalProduct = includesEveryFactor(s1, [
      [`cos(${cfg.m}x)`, `cos(${cfg.m}*x)`],
      cosHalfArgumentNormCandidates(cfg.m + 1),
      cosHalfArgumentNormCandidates(cfg.m - 1),
    ]);
    step1Ok =
      (hasCosMx && hasCosX && hasCosSq) ||
      hasFactoredSum ||
      hasFinalProduct ||
      s1.includes(normalizeEqLike(cfg.step1Latex));
  } else {
    const hasSinMx = s1.includes(`sin(${cfg.m}x)`) || s1.includes(`sin(${cfg.m}*x)`);
    const hasCosMx = s1.includes(`cos(${cfg.m}x)`) || s1.includes(`cos(${cfg.m}*x)`);
    const cosM1 = cfg.m - 1 === 1 ? "cosx" : `cos(${cfg.m - 1}x)`;
    const hasCosM1 =
      s1.includes(cosM1) ||
      s1.includes(`cos(${cfg.m - 1}*x)`) ||
      (cfg.m - 1 === 1 && s1.includes("cos(x)"));
    const hasFactoredSum =
      hasSinMx &&
      hasCosMx &&
      hasCosM1;
    const hasFinalProduct = includesEveryFactor(s1, [
      [`sin(${cfg.m}x)`, `sin(${cfg.m}*x)`],
      cosHalfArgumentNormCandidates(2 * cfg.m - 1),
      ["cos(x/2)", "cos((x)/(2))", "cos(1x/2)"],
    ]);
    step1Ok = s1.includes(normalizeEqLike(cfg.step1Latex)) || hasFactoredSum || hasFinalProduct;
  }

  // STEP 2
  const eqsRaw = Array.isArray(userAnswer.eqs) ? userAnswer.eqs : [];
  const eqsNorm = eqsRaw.map((e) => normalizeEqLike(safeStr(e))).filter((x) => x.length > 0);
  const step2Ok = step2Check(eqsNorm, cfg);

  // STEP 3
  const famsRaw = Array.isArray(userAnswer.fams) ? userAnswer.fams : [];
  const famObjs = famsRaw
    .filter((f) => f && typeof f === "object")
    .filter(
      (f) =>
        String(f.raw ?? "").trim() ||
        String(f.a ?? "").trim() ||
        String(f.b ?? "").trim() ||
        String(f.affine?.aRaw ?? "").trim() ||
        String(f.affine?.bRaw ?? "").trim()
    );

  const hasWrongFam = famObjs.some((f) => !isAllowedFamObj(f, cfg));
  const correctFamCount = famObjs.filter((f) => isAllowedFamObj(f, cfg)).length;
  const step3Ok = !hasWrongFam && correctFamCount >= cfg.families.length;

  // STEP 4 (set equality in interval)
  const xs = Array.isArray(userAnswer.xs) ? userAnswer.xs : [];
  const parsed = xs.map((v) => parsePiMultiple(safeStr(v))).filter((v) => v != null);
  const uniqUser = uniqueRats(parsed);

  const expected = solutions.map((r) => normRat(r.num, r.den));
  const correctCount = uniqUser.filter((u) => expected.some((e) => ratEq(e, u))).length;
  const wrongCount = uniqUser.filter((u) => !expected.some((e) => ratEq(e, u))).length;

  const step4Ok = correctCount === expected.length && wrongCount === 0
    && xs.filter((v) => safeStr(v).trim()).every((v) => parsePiMultiple(safeStr(v)) != null);

  let score = 0;
  if (step1Ok) score += 1;
  if (step2Ok) score += 1;
  if (step3Ok) score += 1;
  if (step4Ok) score += 1;

  score = Math.max(0, Math.min(maxPoints, score));

  return {
    isCorrect: step1Ok && step2Ok && step3Ok && step4Ok,
    score,
    maxScore: maxPoints,
    parts: {
      "krok 1: poprawne przekształcenie": step1Ok,
      "krok 2: poprawne równania do rozwiązania": step2Ok,
      "krok 3: poprawne rozwiązania ogólne": step3Ok,
      [`krok 4: rozwiązania w przedziale ${intervalText(interval)}`]: step4Ok,
    },
  };
}

/* =========================
   Render correct answer
   ========================= */
export function renderAnswer({ data }) {
  const { cfg, interval, solutions } = data;

  const listLatex = solutions.map((r) => ratToLatexPi(r)).join(",\\; ");

  const famLatex = cfg.families
    .map((fam) => `x=${ratToLatexPi(fam.a)}+k\\cdot${ratToLatexPi(fam.b)}`)
    .join("\\;\\lor\\;");

  if (cfg.kind === "quarticIdentity") {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 1:</div>
          <InlineMath math={cfg.sin2PolynomialLatex} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 2:</div>
          <InlineMath math={cfg.sin2FactoredLatex + "\\quad\\Rightarrow\\quad " + cfg.sinDoubleLatex} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 3:</div>
          <InlineMath math={famLatex} />
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 6 }}>
            <InlineMath math={'k\\in\\mathbb{Z}'} />
          </div>
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 4:</div>
          <InlineMath math={"x\\in\\left\\{" + listLatex + "\\right\\}"} />
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
            <InlineMath math={intervalLatex(interval)} />
          </div>
        </div>
      </div>
    );
  }

  if (cfg.kind === "tanQuadratic") {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 1:</div>
          <InlineMath math={cfg.tanEquationLatex} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 2:</div>
          <InlineMath math={cfg.alternativeLatex} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 3:</div>
          <InlineMath math={famLatex} />
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 6 }}>
            <InlineMath math={'k\\in\\mathbb{Z}'} />
          </div>
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 4:</div>
          <InlineMath math={"x\\in\\left\\{" + listLatex + "\\right\\}"} />
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
            <InlineMath math={intervalLatex(interval)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 1:</div>
        <div>
          <InlineMath math={cfg.finalProductLatex || cfg.step1Latex} />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 2:</div>
        <div>
          <InlineMath
            math={`${cfg.eq2_base[0]}\\;\\lor\\;${cfg.eq2_split[0]}\\;\\lor\\;${cfg.eq2_split[1]}`}
          />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 3:</div>
        <div>
          <InlineMath math={famLatex} />
        </div>
        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 6 }}>
          <InlineMath math={'k\\in\\mathbb{Z}'} />
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 4:</div>
        <div>
          <InlineMath math={`x\\in\\left\\{${listLatex}\\right\\}`} />
        </div>
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
          <InlineMath math={intervalLatex(interval)} />
        </div>
      </div>
    </div>
  );
}

/* =========================
   Explanation
   ========================= */

function TanQuadraticExplanation({ cfg, interval, solutions }) {
  const listLatex = solutions.map((r) => ratToLatexPi(r)).join(",\\; ");
  const famLatex = cfg.families
    .map((fam) => "x=" + ratToLatexPi(fam.a) + "+k\\cdot" + ratToLatexPi(fam.b))
    .join("\\;\\lor\\;");
  const middleSign = cfg.sign > 0 ? "+" : "-";
  const isUnit = cfg.tanFamily === "unit";
  const statementLatex = isUnit
    ? cfg.equationLatex.replaceAll("x", " x")
    : "3\\cos^2 x " + middleSign + " \\sqrt{3}\\sin(2x)-3\\sin^2 x=0";
  const expandedLatex = isUnit
    ? statementLatex
    : "3\\cos^2 x " + middleSign + " 2\\sqrt{3}\\sin x\\cos x-3\\sin^2 x=0";
  const dividedLatex = isUnit
    ? cfg.sign > 0
      ? "1-\\dfrac{\\sin^2 x}{\\cos^2 x}=0"
      : "\\dfrac{\\sin^2 x}{\\cos^2 x}-1=0"
    : "3 " +
      middleSign +
      " 2\\sqrt{3}\\cdot\\dfrac{\\sin x}{\\cos x}-3\\cdot\\dfrac{\\sin^2 x}{\\cos^2 x}=0";
  const doubleAngleEquation = isUnit
    ? cfg.sign > 0
      ? "\\cos(2x)=0"
      : "-\\cos(2x)=0"
    : "3\\cos(2x) " + middleSign + " \\sqrt{3}\\sin(2x)=0";
  const tan2Equation = isUnit
    ? "\\cos(2x)=0"
    : cfg.sign > 0
      ? "\\operatorname{tg}(2x)=-\\sqrt{3}"
      : "\\operatorname{tg}(2x)=\\sqrt{3}";
  const doubleFamilyLatex = cfg.doubleFamily
    ? "x=" + ratToLatexPi(cfg.doubleFamily.a) + "+k\\cdot" + ratToLatexPi(cfg.doubleFamily.b)
    : cfg.sign > 0
      ? "x=-\\dfrac{\\pi}{6}+k\\cdot\\dfrac{\\pi}{2}"
      : "x=\\dfrac{\\pi}{6}+k\\cdot\\dfrac{\\pi}{2}";
  const squareLatex =
    isUnit
      ? cfg.sign > 0
        ? "\\cos^2 x-\\sin^2 x=0"
        : "\\sin^2 x-\\cos^2 x=0"
      : cfg.sign > 0
        ? "(\\sqrt{3}\\cos x+\\sin x)^2-4\\sin^2 x=0"
        : "(\\sqrt{3}\\cos x-\\sin x)^2-4\\sin^2 x=0";
  const unitLinearEquationsLatex =
    cfg.sign > 0
      ? "\\cos x-\\sin x=0\\;\\lor\\;\\cos x+\\sin x=0"
      : "\\sin x-\\cos x=0\\;\\lor\\;\\sin x+\\cos x=0";
  const unitDivideLatex =
    cfg.sign > 0
      ? "1-\\operatorname{tg}x=0\\;\\lor\\;1+\\operatorname{tg}x=0"
      : "\\operatorname{tg}x-1=0\\;\\lor\\;\\operatorname{tg}x+1=0";
  const stepTitleStyle = { fontWeight: 700, marginTop: 12, marginBottom: 4 };
  const intervalSolutions = "x\\in\\left\\{" + listLatex + "\\right\\}";
  const integerNote = ",\\quad k\\in\\mathbb{Z}";

  const methods = [
    {
      key: "tan",
      label: (
        <>
          przez <InlineMath math={"\\cos^2 x"} />
        </>
      ),
      content: (
        <div>
          <p style={stepTitleStyle}>{"Spos\u00f3b I. Krok 1. Sprawdzamy, czy mo\u017cna dzieli\u0107 przez "}<InlineMath math={"\\cos^2 x"} />.</p>
          <p>
            {"Gdy "}<InlineMath math={"\\cos x=0"} />{" mamy "}<InlineMath math={"\\sin^2 x=1"} />{" i lewa strona r\u00f3wnania nie jest r\u00f3wna zeru. Mo\u017cemy wi\u0119c rozpatrywa\u0107 przypadek "}<InlineMath math={"\\cos x\\neq0"} />.
          </p>

          <p style={stepTitleStyle}>{isUnit ? "Spos\u00f3b I. Krok 2. Dzielimy przez " : "Spos\u00f3b I. Krok 2. Rozwijamy "}{!isUnit && <InlineMath math={"\\sin(2x)"} />}{!isUnit && " i dzielimy przez "}<InlineMath math={"\\cos^2 x"} />.</p>
          {!isUnit && <BlockMath math={"\\sin(2x)=2\\sin x\\cos x"} />}
          {!isUnit && <BlockMath math={expandedLatex} />}
          <BlockMath math={"\\operatorname{tg}x=\\dfrac{\\sin x}{\\cos x}"} />
          <BlockMath math={dividedLatex} />

          <p style={stepTitleStyle}>{"Spos\u00f3b I. Krok 3. Otrzymujemy r\u00f3wnanie kwadratowe wzgl\u0119dem tangensa."}</p>
          <BlockMath math={cfg.tanEquationLatex} />

          <p style={stepTitleStyle}>{"Spos\u00f3b I. Krok 4. Rozwi\u0105zujemy r\u00f3wnanie kwadratowe i otrzymujemy r\u00f3wnania alternatywne."}</p>
          <BlockMath math={cfg.alternativeLatex} />

          <p style={stepTitleStyle}>{"Spos\u00f3b I. Krok 5. Zapisujemy rozwi\u0105zania og\u00f3lne."}</p>
          <BlockMath math={famLatex + integerNote} />

          <p style={stepTitleStyle}>{"Spos\u00f3b I. Krok 6. Wybieramy rozwi\u0105zania z przedzia\u0142u "}<InlineMath math={intervalLatex(interval)} />.</p>
          <BlockMath math={intervalSolutions} />
        </div>
      ),
    },
    {
      key: "double",
      label: "k\u0105t podwojony",
      content: (
        <div>
          <p style={stepTitleStyle}>{"Spos\u00f3b II. Krok 1. Korzystamy ze wzoru na cosinus podwojonego k\u0105ta."}</p>
          <BlockMath math={"\\cos(2x)=\\cos^2 x-\\sin^2 x"} />
          <BlockMath math={statementLatex} />

          <p style={stepTitleStyle}>{"Spos\u00f3b II. Krok 2. Przepisujemy r\u00f3wnanie w funkcjach k\u0105ta "}<InlineMath math={"2x"} />.</p>
          <BlockMath math={doubleAngleEquation} />

          <p style={stepTitleStyle}>{isUnit ? "Spos\u00f3b II. Krok 3. Rozwi\u0105zujemy r\u00f3wnanie trygonometryczne." : "Spos\u00f3b II. Krok 3. Dzielimy przez "}{!isUnit && <InlineMath math={"\\cos(2x)"} />}{!isUnit && "."}</p>
          {!isUnit && (
            <p>
              {"Dla "}<InlineMath math={"\\cos(2x)=0"} />{" lewa strona nie jest r\u00f3wna zeru, wi\u0119c mo\u017cemy wykona\u0107 dzielenie."}
            </p>
          )}
          <BlockMath math={tan2Equation} />

          <p style={stepTitleStyle}>
            {isUnit ? (
              "Spos\u00f3b II. Krok 4. Zapisujemy rozwi\u0105zania og\u00f3lne."
            ) : (
              <>
                {"Spos\u00f3b II. Krok 4. Rozwi\u0105zujemy r\u00f3wnanie z argumentem "}
                <InlineMath math={"2x"} />.
              </>
            )}
          </p>
          <BlockMath math={doubleFamilyLatex + integerNote} />

          <p style={stepTitleStyle}>{"Spos\u00f3b II. Krok 5. Wybieramy rozwi\u0105zania z przedzia\u0142u "}<InlineMath math={intervalLatex(interval)} />.</p>
          <BlockMath math={intervalSolutions} />
        </div>
      ),
    },
    {
      key: "factor",
      label: "rozk\u0142ad na czynniki",
      content: (
        <div>
          <p style={stepTitleStyle}>{isUnit ? "Spos\u00f3b III. Krok 1. Korzystamy z r\u00f3\u017cnicy kwadrat\u00f3w." : "Spos\u00f3b III. Krok 1. Korzystamy ze wzoru na sinus podwojonego k\u0105ta."}</p>
          {!isUnit && <BlockMath math={"\\sin(2x)=2\\sin x\\cos x"} />}
          {!isUnit && <BlockMath math={expandedLatex} />}

          <p style={stepTitleStyle}>{isUnit ? "Spos\u00f3b III. Krok 2. Zapisujemy lew\u0105 stron\u0119 jako iloczyn." : "Spos\u00f3b III. Krok 2. Zapisujemy lew\u0105 stron\u0119 jako r\u00f3\u017cnic\u0119 kwadrat\u00f3w."}</p>
          <BlockMath math={squareLatex} />
          <BlockMath math={tanQuadraticFactorLatex(cfg)} />

          <p style={stepTitleStyle}>{isUnit ? "Spos\u00f3b III. Krok 3. Z iloczynu r\u00f3wnego zero dostajemy r\u00f3wnania liniowe." : "Spos\u00f3b III. Krok 3. Rozk\u0142adamy wyra\u017cenie na czynniki."}</p>
          <BlockMath math={isUnit ? unitLinearEquationsLatex : tanQuadraticFactorLatex(cfg)} />

          <p style={stepTitleStyle}>{"Spos\u00f3b III. Krok 4. Dzielimy r\u00f3wnania liniowe przez "}<InlineMath math={"\\cos x"} />{" i przechodzimy do tangensa."}</p>
          {isUnit && <BlockMath math={unitDivideLatex} />}
          <BlockMath math={cfg.alternativeLatex} />

          <p style={stepTitleStyle}>{"Spos\u00f3b III. Krok 5. Zapisujemy rozwi\u0105zania og\u00f3lne."}</p>
          <BlockMath math={famLatex + integerNote} />

          <p style={stepTitleStyle}>{"Spos\u00f3b III. Krok 6. Wybieramy rozwi\u0105zania z przedzia\u0142u "}<InlineMath math={intervalLatex(interval)} />.</p>
          <BlockMath math={intervalSolutions} />
        </div>
      ),
    },
  ];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <ExplanationMethodTabs methods={methods} initialKey="tan" />
    </div>
  );
}
export function renderExplanation({ data }) {
  const { cfg, interval, solutions } = data;
  const listLatex = solutions.map((r) => ratToLatexPi(r)).join(",\\; ");
  const famLatex = cfg.families
    .map((fam) => `x=${ratToLatexPi(fam.a)}+k\\cdot${ratToLatexPi(fam.b)}`)
    .join("\\;\\lor\\;");

  if (cfg.kind === "tanQuadratic") {
    return <TanQuadraticExplanation cfg={cfg} interval={interval} solutions={solutions} />;
  }

  if (cfg.kind === "quarticIdentity") {
    const signText = cfg.sign > 0 ? "" : "-";
    const rejectedSinLatex = cfg.sign > 0 ? "\\sin(2x)=-2" : "\\sin(2x)=2";
    const familyLatex = `x=${ratToLatexPi(cfg.families[0].a)}+k\\cdot${ratToLatexPi(cfg.families[0].b)}`;
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p>
          <strong>Krok 1.</strong>{" "}
          {"Przenosimy sk\u0142adniki tak, aby po jednej stronie by\u0142a suma czwartych pot\u0119g:"}
        </p>
        <BlockMath math={cfg.sumLatex} />

        <p>
          <strong>Krok 2.</strong> {"Korzystamy z zale\u017cno\u015bci "}
          <InlineMath math={"\\sin^2x+\\cos^2x=1"} />:
        </p>
        <BlockMath math={"\\sin^4x+\\cos^4x=(\\sin^2x+\\cos^2x)^2-2\\sin^2x\\cos^2x"} />
        <BlockMath math={"\\sin^4x+\\cos^4x=1-2\\sin^2x\\cos^2x"} />

        <p>
          <strong>Krok 3.</strong> {"Przechodzimy do funkcji "} <InlineMath math={"\\sin(2x)"} />.
        </p>
        <BlockMath math={"\\sin x\\cos x=\\dfrac{\\sin(2x)}{2}"} />
        <BlockMath math={`1-\\dfrac{\\sin^2(2x)}{2}=${signText}\\dfrac{\\sin(2x)}{2}`} />
        <BlockMath math={cfg.sin2PolynomialLatex} />
        <BlockMath math={cfg.sin2FactoredLatex} />
        <p>
          {"Z r\u00f3wnania odrzucamy "}
          <InlineMath math={rejectedSinLatex} />
          {", bo warto\u015bci sinusa nale\u017c\u0105 do przedzia\u0142u "}
          <InlineMath math={"[-1,1]"} />.
        </p>

        <p>
          <strong>Krok 4.</strong> {"Rozwi\u0105zujemy r\u00f3wnanie trygonometryczne:"}
        </p>
        <BlockMath math={cfg.sinDoubleLatex} />
        <BlockMath math={`${familyLatex},\\quad k\\in\\mathbb{Z}`} />

        <p>
          <strong>Krok 5.</strong> {"Wybieramy rozwi\u0105zania nale\u017c\u0105ce do przedzia\u0142u"}{" "}
          <InlineMath math={intervalLatex(interval)} />:
        </p>
        <BlockMath math={`x\\in\\left\\{${listLatex}\\right\\}`} />
      </div>
    );
  }

  if (cfg.kind === "cos") {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p>
          <strong>Krok 1.</strong> Korzystamy ze wzoru na sumę cosinusów:
        </p>
        <BlockMath
          math={`\\cos\\alpha+\\cos\\beta=2\\cos\\frac{\\alpha+\\beta}{2}\\cos\\frac{\\alpha-\\beta}{2}`}
        />
        <BlockMath
          math={`\\cos(${cfg.m - 1}x)+\\cos(${cfg.m + 1}x)=2\\cos(${cfg.m}x)\\cos x`}
        />
        <p>Po podstawieniu do równania otrzymujemy:</p>
        <BlockMath
          math={`2\\cos(${trigArgumentLatex(cfg.m)})\\cos x+2\\cos^2(${trigArgumentLatex(cfg.m)})=0`}
        />

        <p>
          <strong>Krok 2.</strong> Wyłączamy wspólny czynnik:
        </p>
        <BlockMath math={cfg.step1Latex} />
        <BlockMath math={`${cfg.eq2_base[0]}\\quad\\lor\\quad${cfg.eq2_base[1]}`} />

        <p>
          <strong>Krok 3.</strong> Drugie równanie rozkładamy jeszcze wzorem na sumę cosinusów:
        </p>
        <BlockMath
          math={`\\cos x+\\cos(${trigArgumentLatex(cfg.m)})=2\\cos\\left(${cosHalfArgumentLatex(cfg.m + 1)}\\right)\\cos\\left(${cosHalfArgumentLatex(cfg.m - 1)}\\right)`}
        />
        <p>Po rozbiciu drugiego nawiasu mamy równoważnie:</p>
        <BlockMath math={cfg.finalProductLatex} />
        <BlockMath
          math={`${cfg.eq2_base[0]}\\quad\\lor\\quad${cfg.eq2_split[0]}\\quad\\lor\\quad${cfg.eq2_split[1]}`}
        />
        <p>Stąd rozwiązania ogólne mają postać:</p>
        <BlockMath math={`${famLatex},\\quad k\\in\\mathbb{Z}`} />

        <p>
          <strong>Krok 4.</strong> Wybieramy rozwiązania należące do przedziału{" "}
          <InlineMath math={intervalLatex(interval)} />:
        </p>
        <BlockMath math={`x\\in\\left\\{${listLatex}\\right\\}`} />
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Grupujemy składniki i korzystamy ze wzoru na sumę sinusów:
      </p>
      <BlockMath
        math={`\\sin\\alpha+\\sin\\beta=2\\sin\\frac{\\alpha+\\beta}{2}\\cos\\frac{\\alpha-\\beta}{2}`}
      />
      <BlockMath
        math={`\\sin x+\\sin(${trigArgumentLatex(2 * cfg.m - 1)})=2\\sin(${trigArgumentLatex(cfg.m)})\\cos(${trigArgumentLatex(cfg.m - 1)})`}
      />
      <BlockMath
        math={`\\sin(${trigArgumentLatex(2 * cfg.m)})=2\\sin(${trigArgumentLatex(cfg.m)})\\cos(${trigArgumentLatex(cfg.m)})`}
      />
      <p>Po podstawieniu do równania i wyłączeniu wspólnego czynnika otrzymujemy:</p>
      <BlockMath math={cfg.step1Latex} />

      <p>
        <strong>Krok 2.</strong> Z iloczynu równego zero dostajemy równania:
      </p>
      <BlockMath math={`${cfg.eq2_base[0]}\\quad\\lor\\quad${cfg.eq2_base[1]}`} />

      <p>
        <strong>Krok 3.</strong> Drugie równanie rozkładamy dalej wzorem na sumę cosinusów:
      </p>
      <BlockMath
        math={`\\cos\\alpha+\\cos\\beta=2\\cos\\frac{\\alpha+\\beta}{2}\\cos\\frac{\\alpha-\\beta}{2}`}
      />
      <BlockMath
        math={`\\cos(${trigArgumentLatex(cfg.m - 1)})+\\cos(${trigArgumentLatex(cfg.m)})=2\\cos\\left(${cosHalfArgumentLatex(2 * cfg.m - 1)}\\right)\\cos\\left(\\frac{x}{2}\\right)`}
      />
      <p>Po rozbiciu drugiego nawiasu mamy równoważnie:</p>
      <BlockMath math={cfg.finalProductLatex} />
      <BlockMath
        math={`${cfg.eq2_base[0]}\\quad\\lor\\quad${cfg.eq2_split[0]}\\quad\\lor\\quad${cfg.eq2_split[1]}`}
      />
      <p>Stąd rozwiązania ogólne mają postać:</p>
      <BlockMath math={`${famLatex},\\quad k\\in\\mathbb{Z}`} />

      <p>
        <strong>Krok 4.</strong> W przedziale <InlineMath math={intervalLatex(interval)} /> otrzymujemy:
      </p>
      <BlockMath math={`x\\in\\left\\{${listLatex}\\right\\}`} />
    </div>
  );
}

/* =========================
   Render user answer (preview)
   ========================= */
export function renderUserAnswer(userAnswer, task = null) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const toStr = (v) => {
    if (v == null) return "";
    if (typeof v === "object") return String(v.raw ?? v.value ?? "").trim();
    return String(v).trim();
  };
  const equationLatex = (value) =>
    toStr(value)
      .replace(/π/g, "\\pi")
      .replace(/\bpi\b/gi, "\\pi")
      .replace(/\*/g, "\\cdot ")
      .replace(/\bcos(?=\s*\()/gi, "\\cos")
      .replace(/\bsin(?=\s*\()/gi, "\\sin")
      .replace(/\btg(?=\s*\()/gi, "\\operatorname{tg}")
      .replace(/\btan(?=\s*\()/gi, "\\tan");
  const piLatex = (value) => formatPiValueLatex(toStr(value)) || equationLatex(value);
  const familyLatex = (value) => {
    const raw = toStr(value);
    if (raw) {
      return formatAffineLatexPreview(raw, "x", false) || equationLatex(raw);
    }
    const a = toStr(value?.a);
    const b = toStr(value?.b);
    if (!a && !b) return null;
    const aLatex = a ? piLatex(a) : "\\square";
    const bLatex = b ? piLatex(b) : "\\square";
    return `x=${aLatex}+k\\cdot${bLatex}`;
  };

  const step1 = toStr(userAnswer.step1);
  const tanEquation = toStr(userAnswer.tanEquation);
  const sin2Equation = toStr(userAnswer.sin2Equation);
  const eqs = Array.isArray(userAnswer.eqs) ? userAnswer.eqs.map(toStr).filter(Boolean) : [];
  const fams = Array.isArray(userAnswer.fams) ? userAnswer.fams : [];
  const family = userAnswer.family && typeof userAnswer.family === "object" ? familyLatex(userAnswer.family) : familyLatex({ raw: userAnswer.family ?? "" });
  const xs = Array.isArray(userAnswer.xs) ? userAnswer.xs.map(toStr).filter(Boolean) : [];

  const famLines = fams
    .filter((f) => f && typeof f === "object")
    .map(familyLatex)
    .filter(Boolean);

  const allBlank = !step1 && !tanEquation && !sin2Equation && !family && eqs.length === 0 && famLines.length === 0 && xs.length === 0;
  if (allBlank) return <i>(brak odpowiedzi)</i>;
  const isTanQuadratic = task?.data?.cfg?.kind === "tanQuadratic";
  const isQuarticIdentity = task?.data?.cfg?.kind === "quarticIdentity";

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem", lineHeight: 1.6 }}>
      {step1 && !isTanQuadratic && (
        <div style={{ marginBottom: "0.75rem" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 1:</div>
          <div>
            <InlineMath math={equationLatex(step1)} />
          </div>
        </div>
      )}

      {isTanQuadratic && tanEquation && (
        <div style={{ marginBottom: "0.75rem" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 1:</div>
          <div>
            <InlineMath math={equationLatex(tanEquation)} />
          </div>
        </div>
      )}

      {isQuarticIdentity && sin2Equation && (
        <div style={{ marginBottom: "0.75rem" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 2:</div>
          <div>
            <InlineMath math={equationLatex(sin2Equation)} />
          </div>
        </div>
      )}

      {eqs.length > 0 && (
        <div style={{ marginBottom: "0.75rem" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {isTanQuadratic ? "Krok 2:" : "Krok 2:"}
          </div>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {eqs.map((e, i) => (
              <li key={i}>
                <InlineMath math={equationLatex(e)} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {isQuarticIdentity && family && (
        <div style={{ marginBottom: "0.75rem" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Krok 3:</div>
          <InlineMath math={family} />
        </div>
      )}

      {famLines.length > 0 && (
        <div style={{ marginBottom: "0.75rem" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {isTanQuadratic ? "Krok 3:" : "Krok 3:"}
          </div>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {famLines.map((f, i) => (
              <li key={i}>
                <InlineMath math={f} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {xs.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {isTanQuadratic ? "Krok 4:" : "Krok 4:"}
          </div>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {xs.map((x, i) => (
              <li key={i}>
                <InlineMath math={piLatex(x)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default {
  type,
  cosSquaredType,
  sinSumType,
  tanQuadraticType,
  quarticIdentityType,
  generateTask,
  generateCosSquaredTask,
  generateSinSumTask,
  generateTanQuadraticTask,
  generateQuarticIdentityTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
